import { useParams } from "react-router-dom";
import { FormSlugs, getFormSlugForProcedureType, Invoice, Procedure, ProcedureType, Tissue } from "../models";
import { useProcedureTypeQuery, useTissueQuery } from "../queries";
import { useMemo } from "react";

interface IUseCurrentTissue {
    tissue: Tissue | undefined;
    tissueId: number | undefined;
    tissueNumber: string | undefined;
    procedures: Procedure[];
    procedure: Procedure | undefined;
    procedureType: ProcedureType | undefined;
    procedureName: string | undefined;
    procedureFormSlug: FormSlugs | undefined;
    invoice: Invoice | undefined;
    isLoading: boolean;
}

export function useCurrentTissue(id?: number): IUseCurrentTissue {
    const { tissueId, procedureId } = useParams<{ tissueId: string; procedureId: string }>();
    const _tissueId = id ?? tissueId;

    const {
        detailQuery: { data: tissue, isFetching: isTissueFetching, isLoading: isTissueLoading },
    } = useTissueQuery({ id: _tissueId });

    const {
        invoiceQuery: { data: invoice },
    } = useTissueQuery({ queryType: "invoice", id: _tissueId });

    const {
        productCodeQuery: { data: productCodeResponse },
    } = useTissueQuery({ queryType: "productCode", id: _tissueId });

    const {
        listQuery: { data: procedureTypes, isFetching: isProcedureTypeFetching, isLoading: isProcedureTypeLoading },
    } = useProcedureTypeQuery();

    const procedures: Procedure[] = useMemo(
        () => (tissue?.tissueProcedures ?? []).map((tp) => tp.procedure).filter(Boolean) as Procedure[],
        [tissue?.tissueProcedures]
    );
    const procedure = procedures.find((procedure) => procedure.id === Number(procedureId));
    const procedureType = procedureTypes?.find((type) => type.id === procedure?.procedureTypeId);
    const procedureName = useMemo(() => {
        if (procedureId === "new" || !procedureId) return "";
        return procedureType?.name ?? "Unknown";
    }, [procedureId, procedureType?.name]);

    const procedureFormSlug = useMemo(() => {
        if (!procedureType) return undefined;
        return getFormSlugForProcedureType(procedureType.name);
    }, [procedureType]);

    const isLoading = isTissueFetching || isTissueLoading || isProcedureTypeFetching || isProcedureTypeLoading;

    return useMemo(
        () => ({
            tissue,
            tissueId: tissue?.id,
            tissueNumber: tissue?.number,
            procedures,
            procedure,
            procedureType,
            procedureName,
            procedureFormSlug,
            invoice,
            productCode: productCodeResponse?.productCode,
            isLoading,
        }),
        [
            invoice,
            isLoading,
            procedure,
            procedureFormSlug,
            procedureName,
            procedureType,
            procedures,
            productCodeResponse?.productCode,
            tissue,
        ]
    );
}
